import { parsePhase, type Phase } from "@/lib/sessions/phase";

export const DB_NAME = "surf-clips-ai";
// v8 migrates the pre-unification session phase literals in place during the
// upgrade, so the read path trusts stored phases directly (no read-time
// translation). In-progress uploads are preserved rather than reset.
export const DB_VERSION = 8;
export const FILE_STORE_NAME = "draft-files";
export const THUMBNAIL_STORE_NAME = "draft-thumbnails";
export const MANIFEST_STORE_NAME = "upload-manifests";
export const SESSION_RECORD_STORE_NAME = "session-records";

// Stores that hold per-session data. v6 makes the session record the single
// home for the session token (manifests no longer carry it), so prior session
// data is dropped rather than migrated.
const SESSION_STORE_NAMES = [MANIFEST_STORE_NAME, SESSION_RECORD_STORE_NAME];

function ensureObjectStores(db: IDBDatabase) {
  if (!db.objectStoreNames.contains(FILE_STORE_NAME)) {
    db.createObjectStore(FILE_STORE_NAME, { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains(THUMBNAIL_STORE_NAME)) {
    db.createObjectStore(THUMBNAIL_STORE_NAME, { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains(MANIFEST_STORE_NAME)) {
    db.createObjectStore(MANIFEST_STORE_NAME, { keyPath: "clientSessionId" });
  }
  if (!db.objectStoreNames.contains(SESSION_RECORD_STORE_NAME)) {
    db.createObjectStore(SESSION_RECORD_STORE_NAME, {
      keyPath: "clientSessionId",
    });
  }
}

function resetSessionStores(db: IDBDatabase) {
  for (const name of SESSION_STORE_NAMES) {
    if (db.objectStoreNames.contains(name)) {
      db.deleteObjectStore(name);
    }
  }
}

// Pre-v8 session phase literals, translated once during the v6/v7 → v8 upgrade.
const LEGACY_PHASE_BY_STORED: Record<string, Phase> = {
  uploading: "upload",
  ready: "processing_pending",
  processing: "grouping",
  done: "complete",
};

function migrateSessionRecordPhases(transaction: IDBTransaction) {
  const request = transaction
    .objectStore(SESSION_RECORD_STORE_NAME)
    .openCursor();
  request.onsuccess = () => {
    const cursor = request.result;
    if (!cursor) return;
    const record = cursor.value as Record<string, unknown> & { phase?: string };
    const stored = record.phase;
    const migrated = LEGACY_PHASE_BY_STORED[stored ?? ""] ?? parsePhase(stored);
    if (migrated !== stored) {
      cursor.update({ ...record, phase: migrated });
    }
    cursor.continue();
  };
}

function openSurfClipsAIDbInternal() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error ?? new Error("IndexedDB open failed"));
    };

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;

      // Upgrading from any pre-v6 schema: discard prior session data so the
      // session record can be re-established as the sole token authority.
      if (oldVersion > 0 && oldVersion < 6) {
        resetSessionStores(db);
      }

      ensureObjectStores(db);

      // v6/v7 records keep legacy phase literals; translate them once now so the
      // read path no longer needs a compatibility shim.
      const upgradeTx = request.transaction;
      if (upgradeTx && oldVersion >= 6 && oldVersion < 8) {
        migrateSessionRecordPhases(upgradeTx);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onblocked = () => {
      reject(new Error("IndexedDB upgrade blocked by another open connection"));
    };
  });
}

export function openSurfClipsAIDb() {
  return openSurfClipsAIDbInternal();
}
